// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Del op's "Flere værktøjer": the parts of Segmentering a quick cut-out does
 * not need, folded under one disclosure so the tile stays a picture first.
 *
 *  - Prompts — every drawn point/box with an optional class name, plus
 *    text-only prompts ("dør" finds every door; with a box, only those in
 *    it), and the label library's texts as one-click chips;
 *  - Konfidens — the run's detection threshold;
 *  - Tilføj til kø — the same prompts on the neighbouring frames, staged in
 *    the label queue (`LabelQueueContext`) and run from Billeder.
 *
 * Presentational over the parent's prompt and confidence state; it owns only
 * the queue's Før/Efter counts.
 */

import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../../../api/client';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { useLabelQueue } from '../../../app/LabelQueueContext';
import { useAsync } from '../../../app/useAsync';
import {
  confidenceText,
  optionsSummary,
  queueButtonLabel,
  queueTargets,
  unusedLibraryTexts,
  withTextPrompt,
} from '../../../bygning/delop';
import { buildRequestPrompts, clampNeighborCount, NEIGHBOR_MAX, type SegPrompt } from '../../../data/segmentView';
import { Button } from '../../Button';
import { IconButton, XIcon } from '../../IconButton';
import { TextInput } from '../../TextInput';
import { TextLink } from '../../TextLink';
import styles from './SegmentOptions.module.css';

export interface SegmentOptionsProps {
  frameId: number;
  prompts: readonly SegPrompt[];
  onPrompts: (next: SegPrompt[]) => void;
  confidence: number;
  onConfidence: (value: number) => void;
  /** Prompt id → mask slot (label value), for the swatches. */
  slotOf: (id: string) => number | null;
  paletteSize: number;
  newId: () => string;
  disabled: boolean;
  /** Called after staging, with the count (the parent toasts). */
  onQueued: (count: number) => void;
}

export function SegmentOptions({
  frameId,
  prompts,
  onPrompts,
  confidence,
  onConfidence,
  slotOf,
  paletteSize,
  newId,
  disabled,
  onQueued,
}: SegmentOptionsProps) {
  const canEdit = useCanEdit();
  const queue = useLabelQueue();
  const [open, setOpen] = useState(false);
  const [before, setBefore] = useState(0);
  const [after, setAfter] = useState(0);

  // The scan's frame order, for the neighbours: fetched once the panel opens.
  const ids = useAsync((signal) => (open ? api.frames({}, signal).then((f) => f.ids) : Promise.resolve(null)), [open]);
  const targets = queueTargets(ids.data ?? null, frameId, before, after);
  const chips = unusedLibraryTexts(queue.labels, prompts);

  const setText = (id: string, text: string) => onPrompts(prompts.map((p) => (p.id === id ? { ...p, text } : p)));
  const remove = (id: string) => onPrompts(prompts.filter((p) => p.id !== id));

  const enqueue = () => {
    queue.enqueue(targets, buildRequestPrompts(prompts).prompts, confidence);
    onQueued(targets.length);
  };

  return (
    <details className={styles.more} open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className={styles.summary}>
        <span className={styles.summaryTitle}>Flere værktøjer</span>
        <span className={styles.muted}>{optionsSummary(prompts, confidence, queue.pendingCount)}</span>
      </summary>

      <div className={styles.body}>
        <section className={styles.group} aria-label="Prompts">
          <p className={styles.hint}>
            Skriv et klassenavn (fx dør) for at finde alle af slagsen. Sammen med et punkt eller en boks navngiver det
            markeringen.
          </p>
          {prompts.length > 0 && (
            <ol className={styles.prompts}>
              {prompts.map((p) => {
                const slot = slotOf(p.id);
                return (
                  <li key={p.id} className={styles.prompt}>
                    <span
                      className={styles.swatch}
                      data-slot={slot === null ? 'none' : undefined}
                      style={slot === null ? undefined : ({ '--c': `var(--label-${slot % paletteSize})` } as CSSProperties)}
                      title={p.box ? (p.point ? 'Punkt' : 'Boks') : 'Kun tekst'}
                    >
                      {slot === null ? '·' : slot + 1}
                    </span>
                    <span className={styles.kind} aria-hidden="true">
                      {p.box ? (p.point ? '•' : '▭') : 'T'}
                    </span>
                    <TextInput
                      variant="outline"
                      size="sm"
                      className={styles.input}
                      value={p.text}
                      disabled={disabled}
                      placeholder={p.box ? 'klasse (valgfri)' : 'klasse, fx dør'}
                      aria-label={`Klassenavn for prompt ${slot === null ? '' : slot + 1}`}
                      onChange={(e) => setText(p.id, e.target.value)}
                    />
                    <IconButton
                      variant="ghost"
                      shape="circle"
                      label="Fjern prompt"
                      onClick={() => remove(p.id)}
                      disabled={disabled}
                    >
                      <XIcon />
                    </IconButton>
                  </li>
                );
              })}
            </ol>
          )}
          <div className={styles.row}>
            <TextLink size="sm" disabled={disabled} onClick={() => onPrompts(withTextPrompt(prompts, newId()))}>
              + Tekstprompt
            </TextLink>
            {chips.map((text) => (
              <button
                key={text}
                type="button"
                className={styles.chip}
                disabled={disabled}
                title="Fra etiketbiblioteket"
                onClick={() => onPrompts(withTextPrompt(prompts, newId(), text))}
              >
                + {text}
              </button>
            ))}
          </div>
        </section>

        <label className={styles.slider}>
          <span>
            Konfidens <span className={styles.mono}>{confidenceText(confidence)}</span>
          </span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={confidence}
            disabled={disabled}
            onChange={(e) => onConfidence(Number(e.target.value))}
          />
        </label>

        {canEdit && (
          <section className={styles.group} aria-label="Tilføj til kø">
            <p className={styles.hint}>
              Samme prompts på nabobillederne — køen køres samlet fra <Link to="/frames">Billeder</Link>
              {queue.pendingCount > 0 ? ` (${queue.pendingCount} venter)` : ''}.
            </p>
            <div className={styles.row}>
              <label className={styles.num}>
                Før
                <TextInput
                  variant="outline"
                  size="sm"
                  className={styles.input}
                  type="number"
                  min={0}
                  max={NEIGHBOR_MAX}
                  value={before}
                  onChange={(e) => setBefore(clampNeighborCount(e.target.value))}
                />
              </label>
              <label className={styles.num}>
                Efter
                <TextInput
                  variant="outline"
                  size="sm"
                  className={styles.input}
                  type="number"
                  min={0}
                  max={NEIGHBOR_MAX}
                  value={after}
                  onChange={(e) => setAfter(clampNeighborCount(e.target.value))}
                />
              </label>
              <Button onClick={enqueue} disabled={disabled || ids.loading}>
                {queueButtonLabel(targets.length)}
              </Button>
            </div>
          </section>
        )}
      </div>
    </details>
  );
}
